import { useEffect } from "react";
import { DetailSection } from "@/components/shared/detail-section";
import { StatusPill } from "@/components/shared/status-badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAdHocFeedbackForPerson,
  useMarkFeedbackSeen,
  useSetAdHocFeedbackVisibility,
} from "@/hooks/use-adhoc-feedback";
import { formatDateOnly } from "@/lib/dates";

/**
 * Standalone feedback about this person, given outside any review cycle —
 * EPIC-067 WI-246/WI-248. Also holds post-project feedback (EPIC-068
 * WI-250/251), distinguished by a project tag rather than a separate list —
 * same row shape, same visibility rules (AC-720).
 *
 * The server decides the role (self / manager / none) and returns nothing at
 * all for anyone else, so this component only ever has two shapes to render:
 * the subject's own released notes, or their manager's full view with a
 * release toggle on each one. Renders nothing when there's nothing to show —
 * same posture as every other optional section on a person's profile.
 *
 * On your own profile, notes shared since you last looked carry a "New" pill,
 * and showing them here is what marks them read — which clears the badge on
 * the Reviews tab and the card on My Dashboard. The pills stay for the rest of
 * this visit, because the list itself isn't refetched.
 */
export function PersonAdHocFeedback({ personId }: { personId: string }) {
  const { data, isLoading } = useAdHocFeedbackForPerson(personId);
  const setVisibility = useSetAdHocFeedbackVisibility(personId);
  const markSeen = useMarkFeedbackSeen();
  const { mutate: markSeenMutate } = markSeen;
  // A string, so the effect fires once per distinct set of new notes rather
  // than on every render that hands back an equal-but-new array.
  const newIds = (data?.items ?? [])
    .filter((n) => n.isNew)
    .map((n) => n.id)
    .join(",");
  useEffect(() => {
    if (newIds) markSeenMutate(newIds.split(","));
  }, [newIds, markSeenMutate]);

  if (isLoading) return <Skeleton className="h-24 w-full" />;
  if (!data || data.role === "none") return null;
  // Someone who can't act on this feedback and has none to read isn't shown a
  // section at all. A manager or admin is: an empty list says "nobody has
  // written anything", not "this isn't a feature you have".
  if (data.items.length === 0 && data.role === "self") return null;

  return (
    <DetailSection title="Feedback given outside a review">
      {data.items.length === 0 && (
        <p className="text-muted-foreground text-sm">
          No one has given feedback about this person outside a review yet.
        </p>
      )}
      {data.role === "manager" && data.items.some((n) => !n.isVisibleToEmployee) && (
        <p className="mb-2 text-muted-foreground text-sm">
          Notes stay private to you until you tick "Share with them". Nothing is shared by default.
        </p>
      )}
      <ul className="space-y-2">
        {data.items.map((note) => (
          <li key={note.id} className="rounded-lg border border-border p-3">
            {(note.isNew || note.relatedProjectName) && (
              <div className="mb-1.5 flex flex-wrap gap-1.5">
                {note.isNew && <StatusPill tone="info" label="New" />}
                {note.relatedProjectName && (
                  <StatusPill tone="neutral" label={note.relatedProjectName} />
                )}
              </div>
            )}
            <p className="whitespace-pre-line text-sm">{note.body}</p>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <span className="text-muted-foreground text-xs">
                {note.isAnonymous ? "Unsigned" : (note.authorName ?? "A colleague")} ·{" "}
                {note.sentimentName} · {formatDateOnly(note.createdAt)}
              </span>
              {data.role === "manager" && (
                <Label
                  htmlFor={`adhoc-share-${note.id}`}
                  className="flex items-center gap-2 font-normal text-xs"
                >
                  <Checkbox
                    id={`adhoc-share-${note.id}`}
                    checked={note.isVisibleToEmployee}
                    disabled={setVisibility.isPending}
                    onCheckedChange={(checked) =>
                      setVisibility.mutate({
                        feedbackId: note.id,
                        isVisibleToEmployee: checked === true,
                      })
                    }
                  />
                  Share with them
                </Label>
              )}
            </div>
          </li>
        ))}
      </ul>
    </DetailSection>
  );
}
